/**
 * Platform Billing Page - Admin
 * Overview of all tenant billing, revenue, and invoice management
 */

import { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Receipt,
  ArrowUpRight,
  Download,
  FileText,
  Send,
  Eye,
  Loader2,
  RefreshCw,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { useAllTenants } from '../../hooks/useTenant';
import {
  useAdminAllInvoices,
  useAdminInvoicePreview,
  useAdminGenerateInvoice,
} from '../../hooks/useInvoices';

function MetricCard({ title, value, subtitle, icon: Icon, color }) {
  const colorClasses = {
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  };

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-green-600 flex items-center gap-1 mt-2">
              <ArrowUpRight className="w-4 h-4" /> {subtitle}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const config = {
    paid: { variant: 'success', icon: CheckCircle, label: 'Paid' },
    pending: { variant: 'warning', icon: Clock, label: 'Pending' },
    overdue: { variant: 'danger', icon: AlertCircle, label: 'Overdue' },
    draft: { variant: 'default', icon: FileText, label: 'Draft' },
  };
  const cfg = config[status] || config.pending;
  const Icon = cfg.icon;

  return (
    <Badge variant={cfg.variant} className="inline-flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

function InvoiceGeneratorModal({ tenants, onClose }) {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [sendEmail, setSendEmail] = useState(true);

  const { data: preview, isLoading: previewLoading } = useAdminInvoicePreview(
    selectedClient,
    selectedMonth
  );

  const generateMutation = useAdminGenerateInvoice();

  const handleGenerate = async () => {
    try {
      await generateMutation.mutateAsync({
        clientId: selectedClient,
        month: selectedMonth,
        sendEmail,
      });
      alert('Invoice generated successfully!');
      onClose();
    } catch (error) {
      alert(`Failed to generate invoice: ${error.message}`);
    }
  };

  const currency = preview?.currency === 'USD' ? '$' : '₹';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Generate Invoice
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client
            </label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            >
              <option value="">Select client...</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name || tenant.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Billing Period
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            />
          </div>
        </div>

        {/* Preview */}
        {selectedClient && (
          <Card className="mb-6 bg-gray-50 dark:bg-gray-700/50">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-4">
              Invoice Preview
            </h4>
            {previewLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : preview ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Company</span>
                  <span className="text-sm font-medium">{preview.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Period</span>
                  <span className="text-sm font-medium">{preview.billing_period_display}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Billing Type</span>
                  <span className="text-sm font-medium capitalize">{preview.billing_type?.replace('_', ' ')}</span>
                </div>
                {preview.billing_type === 'post_paid' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Trips</span>
                      <span className="text-sm font-medium">{preview.trips_registered?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Gross Revenue</span>
                      <span className="text-sm font-medium">{currency}{preview.gross_revenue?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Per Trip Charge</span>
                      <span className="text-sm font-medium">{currency}{preview.per_trip_charge?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Revenue Share</span>
                      <span className="text-sm font-medium">{currency}{preview.revenue_share_charge?.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Tokens</span>
                    <span className="text-sm font-medium">{preview.tokens_purchased?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Total</span>
                  <span className="text-lg font-bold text-blue-600">{currency}{preview.total_bill?.toLocaleString()}</span>
                </div>
                {preview.invoice_email && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Will be sent to</span>
                    <span className="text-gray-500">{preview.invoice_email}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Select a client to preview invoice
              </p>
            )}
          </Card>
        )}

        {/* Options */}
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Send invoice via email
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={!selectedClient || generateMutation.isPending}
            className="flex-1"
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Generate Invoice
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PlatformBilling() {
  const { data: tenants = [], isLoading: tenantsLoading } = useAllTenants();
  const { data: invoicesData, isLoading: invoicesLoading, refetch } = useAdminAllInvoices();
  const [showGenerator, setShowGenerator] = useState(false);

  const invoices = invoicesData?.invoices || [];

  // Calculate metrics from real data
  const totalMRR = tenants.reduce((sum, t) => {
    if (t.billing_type === 'post_paid') {
      return sum + (t.trips_count || 0) * 6.5; // Estimated
    }
    return sum + (t.token_balance || 0) * 0.01; // Estimated value
  }, 0);

  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  const pendingInvoices = invoices.filter(inv => inv.status === 'pending');
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');

  const totalCollected = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalOutstanding = [...pendingInvoices, ...overdueInvoices].reduce(
    (sum, inv) => sum + (inv.total || 0),
    0
  );

  const recentTransactions = invoices.slice(0, 5);

  const isLoading = tenantsLoading || invoicesLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Platform Billing</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Revenue and billing overview across all tenants
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowGenerator(true)}>
            <FileText className="w-4 h-4 mr-2" /> Generate Invoice
          </Button>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total MRR"
          value={`$${Math.round(totalMRR).toLocaleString()}`}
          subtitle="+12% from last month"
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          title="Total Collected"
          value={`$${totalCollected.toLocaleString()}`}
          subtitle={`${paidInvoices.length} invoices`}
          icon={CreditCard}
          color="blue"
        />
        <MetricCard
          title="Outstanding"
          value={`$${totalOutstanding.toLocaleString()}`}
          subtitle={`${pendingInvoices.length + overdueInvoices.length} invoices`}
          icon={Receipt}
          color="orange"
        />
        <MetricCard
          title="Overdue"
          value={overdueInvoices.length.toString()}
          subtitle={overdueInvoices.length > 0 ? 'Need attention' : 'All clear'}
          icon={AlertCircle}
          color="purple"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Tenant */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Revenue by Tenant</h3>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : tenants.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No tenants found</p>
          ) : (
            <div className="space-y-4">
              {tenants.slice(0, 5).map((tenant) => {
                const revenue = tenant.billing_type === 'post_paid'
                  ? (tenant.trips_count || 0) * 6.5
                  : (tenant.token_balance || 0) * 0.05;
                const maxRevenue = Math.max(...tenants.map(t =>
                  t.billing_type === 'post_paid' ? (t.trips_count || 0) * 6.5 : (t.token_balance || 0) * 0.05
                ));
                const percentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;

                return (
                  <div key={tenant.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800 dark:text-white">
                        {tenant.name || tenant.id}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        ${Math.round(revenue).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent Invoices */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Recent Invoices</h3>
            <Badge variant="default">{invoices.length} total</Badge>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : recentTransactions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No invoices found</p>
          ) : (
            <div className="space-y-3">
              {recentTransactions.map((inv) => {
                const currency = inv.currency === 'USD' ? '$' : '₹';
                return (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">
                          {inv.client_name || inv.client_id}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {inv.billing_period} &bull; {inv.id}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-800 dark:text-white">
                        {currency}{(inv.total || 0).toLocaleString()}
                      </p>
                      <StatusBadge status={inv.status} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* All Invoices Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">All Invoices</h3>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No invoices found</p>
            <Button variant="primary" className="mt-4" onClick={() => setShowGenerator(true)}>
              <FileText className="w-4 h-4 mr-2" /> Generate First Invoice
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4 font-medium">Invoice</th>
                  <th className="py-3 px-4 font-medium">Client</th>
                  <th className="py-3 px-4 font-medium">Period</th>
                  <th className="py-3 px-4 font-medium">Type</th>
                  <th className="py-3 px-4 font-medium">Amount</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Due Date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const currency = inv.currency === 'USD' ? '$' : '₹';
                  return (
                    <tr
                      key={inv.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-800 dark:text-white">{inv.id}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600 dark:text-gray-300">
                          {inv.client_name || inv.client_id}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600 dark:text-gray-300">{inv.billing_period}</span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={inv.billing_type === 'post_paid' ? 'purple' : 'blue'}>
                          {inv.billing_type === 'post_paid' ? 'Post-Paid' : 'Token'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-800 dark:text-white">
                          {currency}{(inv.total || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={inv.status} />
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600 dark:text-gray-300">{inv.due_date}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Invoice Generator Modal */}
      {showGenerator && (
        <InvoiceGeneratorModal
          tenants={tenants}
          onClose={() => setShowGenerator(false)}
        />
      )}
    </div>
  );
}
